import { EditableProTable } from '@ant-design/pro-components'
import { Button, Form, Popconfirm, Select, Typography, Upload, Modal } from 'antd'
import FormSection from 'components/form-section'
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import { generateUuid } from 'utils/dataUtil'
import { isDecreasing, isIncreasing } from 'utils/number'
import Papa from 'papaparse'
import { TableOutlined } from '@ant-design/icons'
import SOFTableEditor from './SOFTableEditor'
import { useQuery } from '@tanstack/react-query'
import { getReferenceList, getReferenceData } from 'service/completionOptimizer'

import classes from './Table.module.scss'
import { isNumeric } from 'utils/string'

const SOFTable = ({ tab, fields = [], operation }) => {
  const form = Form.useFormInstance()
  const [data, setData] = useState([])
  const [errors, setErrors] = useState([])
  const [isTableEditorOpen, setIsTableEditorOpen] = useState(false)
  const [editingData, setEditingData] = useState([])
  const tableEditorRef = useRef(null)

  const { data: references } = useQuery({
    queryKey: ['references', tab === 'matrix' ? 'scal' : 'fracture', 'sof'],
    queryFn: () => {
      console.log(`Fetching ${tab} SOF references list...`)
      return getReferenceList(tab === 'matrix' ? 'scal' : 'fracture', 'sof').then(data => {
        console.log('SOF Reference List:', {
          group: tab === 'matrix' ? 'scal' : 'fracture',
          type: 'sof',
          data: data
        })
        return data
      })
    }
  })

  const referenceOptions = useMemo(() => {
    if (!references) return []
    return references.map(ref => ({
      label: ref.name,
      value: ref.id
    }))
  }, [references])

  const handleReferenceChange = async (value) => {
    if (!value) return

    try {
      const group = tab === 'matrix' ? 'scal' : 'fracture'
      console.log('Fetching SOF reference data:', {
        group,
        type: 'sof',
        id: value
      })
      
      const referenceData = await getReferenceData(group, 'sof', value)
      console.log('SOF Reference Data:', {
        group,
        type: 'sof',
        id: value,
        data: referenceData
      })

      // Remove existing fields
      const fieldCount = fields.length
      for (let i = fieldCount - 1; i >= 0; i--) {
        operation?.remove?.(i)
      }

      // Convert object to array and filter out length property
      const dataArray = Object.entries(referenceData)
        .filter(([key]) => key !== 'length')
        .map(([_, value]) => value)

      // Add reference data rows
      dataArray.forEach(row => {
        const newRow = {
          SO: row.so,
          KRO: row.kro,
          PSIA: row.psia
        }
        operation?.add(newRow)
      })
    } catch (error) {
      console.error('Error loading reference data:', error)
    }
  }

  const fetchData = () => {
    setData(fields.map((field, index) => ({
      ...form.getFieldValue(["SCAL", tab, "SOF", field.name]),
      key: index
    })) ?? [])
  }

  useEffect(() => {
    validateData(data)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields])

  const columns = useMemo(
    () => [
      {
        key: 'key',
        dataIndex: 'key',
        title: "Row",
        editable: false,
        render: (dom, record, index) => index + 1
      },
      {
        key: 'SO',
        dataIndex: 'SO',
        title: 'So',
        editable: true,
      },
      {
        key: 'KRO',
        dataIndex: 'KRO',
        title: 'Krow',
        editable: true,
      },
      {
        key: 'KRG',
        dataIndex: 'KRG',
        title: 'Krog',
        editable: true,
      },
      {
        title: "Operation",
        valueType: "option",
        key: "operation",
        width: 160,
        render: (text, record, index, action) => [
          <Button
            key="editable"
            type="text"
            className="text-primary"
            onClick={() => {
              action?.startEditable?.(record.key);
            }}
          >
            Edit
          </Button>,
          <Popconfirm
            key="delete"
            title="Sure to delete?"
            onConfirm={() => {
              operation?.remove?.(index);
              const currentSOFData = form.getFieldValue(["SCAL", tab, "SOF"]);
              const newSOFData = currentSOFData.filter((_, i) => i !== index);
              form.setFieldValue(["SCAL", tab, "SOF"], newSOFData);
              fetchData();
            }}
          >
            <Button type="text" danger>Delete</Button>
          </Popconfirm>
        ],
      }
    ], [operation])

  const validateData = useCallback((_data = []) => {
    setErrors([])

    const _errors = []

    if (!_data.length) return;

    const soValues = []
    const kroValues = []
    const pcValues = []

    _data.forEach((row, index) => {
      if (!isNumeric(row.SO)) {
        _errors.push(`Value of So in row #${index + 1} must be a number`)
      } else {
        const so = parseFloat(row.SO)
        if (so < 0 || so > 1) {
          _errors.push(`Value of So in row #${index + 1} must be between 0 and 1`)
        } else soValues.push(so)
      }
      if (!isNumeric(row.KRO)) {
        _errors.push(`Value of Kro in row #${index + 1} must be a number`)
      } else {
        const kro = parseFloat(row.KRO)
        if (kro < 0 || kro > 1) {
          _errors.push(`Value of Kro in row #${index + 1} must be between 0 and 1`)
        } else kroValues.push(kro)
      }
      if (!isNumeric(row.PSIA)) {
        _errors.push(`Value of Pc (psia) in row #${index + 1} must be a number`)
      } else {
        const pc = parseFloat(row.PSIA)
        pcValues.push(pc)
      }
    })

    if (_errors.length) {
      setErrors(_errors)
      return
    }

    const isSoIncreasing = isIncreasing(soValues)
    if (!isSoIncreasing) {
      _errors.push(`The values of So must be increasing`)
    }

    const isKroIncreasing = isIncreasing(kroValues)
    if (!isKroIncreasing) {
      _errors.push(`The values of Kro must be increasing`)
    }

    const isPcDecreasing = isDecreasing(pcValues)
    if (!isPcDecreasing) {
      _errors.push(`The values of Pc (psia) must be decreasing`)
    }

    setErrors(_errors)
  }, [])

  const handleFileUpload = (file) => {
    Papa.parse(file, {
      header: true,
      dynamicTyping: true,
      complete: (result) => {
        const parsedData = result.data
          .filter(row => Object.values(row).some(value => value !== null))
          .map((row, index) => ({
            ...row,
            key: generateUuid(),
          }));

        setData(parsedData);

        fields.forEach((field, index) => {
          if (parsedData[index]) {
            const recordData = {
              SO: parsedData[index].SO,
              KRO: parsedData[index].KRO,
              PSIA: parsedData[index].PSIA
            };
            form.setFieldValue(["SCAL", tab, "SOF", field.name], recordData);
          }
        });

        validateData(parsedData);
      },
    });
    return false;
  };

  const handleTableEditorOpen = () => {
    setEditingData(data);
    setIsTableEditorOpen(true);
  };

  const handleTableEditorCancel = () => {
    setEditingData(data);
    setIsTableEditorOpen(false);
  };

  const handleTableEditorSave = () => {
    const currentEditingData = tableEditorRef.current?.getCurrentData() || [];

    const fieldCount = fields.length;
    for (let i = fieldCount - 1; i >= 0; i--) {
      operation?.remove?.(i);
    }

    currentEditingData.forEach((row) => {
      const newRow = {
        SO: row.SO,
        KRO: row.KRO,
        PSIA: row.PSIA,
        KRG: row.KRG
      };
      operation?.add?.(newRow);
    });

    setData(currentEditingData);
    setIsTableEditorOpen(false);
    fetchData();
  };

  // Add title generator based on columns
  const tableTitle = useMemo(() => {
    return "SOF (So-Kro-Pc)"  // SOF table always has same columns
  }, [])

  return (
    <FormSection
      title={tableTitle}  // Use dynamic title
      extra={
        <div className="d-flex justify-content-end align-items-center gap-2">
          <Button 
            icon={<TableOutlined />}
            onClick={handleTableEditorOpen}
            title="Open Table Editor"
          />
          <Upload
            accept=".csv"
            showUploadList={false}
            beforeUpload={handleFileUpload}
          >
            <Button type="primary">Upload CSV</Button>
          </Upload>
          <Typography.Text className="text-light">Reference</Typography.Text>
          <Form.Item 
            name={['SCAL', tab, 'SOF', 'reference']}
            noStyle
          >
            <Select
              style={{ minWidth: 120 }} 
              dropdownMatchSelectWidth={false}
              options={referenceOptions}
              onChange={handleReferenceChange}
              allowClear
            />
          </Form.Item>
        </div>
      }
      bodyStyle={{
        paddingLeft: 0,
        paddingRight: 0,
        paddingTop: 0
      }}
    >
      <EditableProTable
        options={false}
        pagination={false}
        search={false}
        columns={columns}
        value={data}
        rowKey="key"
        tableExtraRender={() => (
          <ul className="px-24 text-danger">
            {errors.map(errMsg => (
              <li key={errMsg}>
                <Typography.Text type="danger">{errMsg}</Typography.Text>
              </li>
            ))}
          </ul>
        )}
        sticky={{
          offsetHeader: 32
        }}
        className={classes.tableContainer}
        cardProps={{
          className: classes.card
        }}
        tableClassName={classes.table}
        editable={{
          type: 'multiple',
          actionRender: (row, config, defaultDom) => [
            defaultDom.save,
            defaultDom.cancel,
          ],
          onSave: (rowKey, data, row) => {
            const { index, isNew, ...recordData } = data
            if (isNew) {
              operation?.add?.(recordData)
            } else {
              form.setFieldValue(["SCAL", tab, "SOF", index], recordData)
              fetchData()
            }
          }
        }}
        recordCreatorProps={{
          record: {
            key: generateUuid(),
            isNew: true
          }
        }}
      />
      <Modal
        title="Table Editor"
        open={isTableEditorOpen}
        onCancel={handleTableEditorCancel}
        width={800}
        style={{ top: 20 }}
        bodyStyle={{ 
          padding: '12px',
          height: 'calc(80vh - 108px)',
          overflow: 'hidden'
        }}
        footer={[
          <Button key="cancel" onClick={handleTableEditorCancel}>
            Cancel
          </Button>,
          <Button 
            key="save" 
            type="primary" 
            onClick={handleTableEditorSave}
          >
            Save
          </Button>
        ]}
      >
        <div style={{ height: '100%' }}>
          <SOFTableEditor
            ref={tableEditorRef}
            data={editingData}
            columns={columns}
            onDataChange={setEditingData}
          />
        </div>
      </Modal>
    </FormSection>
  )
}

export default SOFTable 